import React, { useState, useEffect, useMemo } from "react";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { useParams } from "react-router-dom";
import "./PublicMenuPage.css";
import awsConfig from "../aws-config";
import { formatCurrency } from "../utils/currency";

const API_URL = awsConfig.api_url_get_menu;

export default function PublicMenuPage() {
  const { slug } = useParams();
  const [menu, setMenu] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [businessInfo, setBusinessInfo] = useState(null);

  // TODO: replace with actual slug→businessId lookup if needed
  const businessId = slug;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_URL}/${businessId}`);
        if (!res.ok) throw new Error(`Failed to load menu (${res.status})`);
        const data = await res.json();
        if (!cancelled) {
          setMenu(data.items || []);
          setBusinessInfo(data.business || null);

          // Extract lastUpdated from any item (all have the same publishedAt)
          if (data.items?.length > 0 && data.items[0].publishedAt) {
            setLastUpdated(data.items[0].publishedAt);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const groupedMenu = menu.reduce((acc, item) => {
    const cat = item.category || "Uncategorised";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});
  const sortedCategories = Object.keys(groupedMenu).sort();

  // Format date
  const formattedDate = lastUpdated
    ? new Date(lastUpdated).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const locationSummary = businessInfo?.address?.city
    ? [businessInfo.address.city, businessInfo.address.country]
        .filter(Boolean)
        .join(", ")
    : businessInfo?.address?.country || "";

  const pageTitle = businessInfo?.name ? `${businessInfo.name} Menu` : "Menu";
  const metaDescription = businessInfo?.name
    ? `Explore ${businessInfo.name}'s menu${
        locationSummary ? " in " + locationSummary : ""
      } on BrandMyKitchen.`
    : "Browse a BrandMyKitchen menu and place your order online.";

  const formattedAddress = useMemo(() => {
    if (!businessInfo?.address) return null;
    const { street, line2, city, postcode, country } = businessInfo.address;
    const parts = [street, line2, city, postcode, country].filter(Boolean);
    return parts.length ? parts.join("\n") : null;
  }, [businessInfo]);

  usePageMetadata({
    title: pageTitle,
    description: metaDescription,
    aiActions: "view",
  });

  return (
    <div className="public-menu-container">
      <h1 className="public-menu-title">{businessInfo?.name || slug} Menu</h1>
      {formattedDate && (
        <div className="public-menu-updated">
          Last updated: {formattedDate}
        </div>
      )}

      {loading && <div className="public-loading">Loading menu…</div>}
      {error && <div className="public-error">⚠️ {error}</div>}

      {!loading && !error && sortedCategories.length === 0 && (
        <div className="public-empty">No menu items available.</div>
      )}

      {businessInfo && (
        <div className="public-business-info">
          <h2>Visit Us</h2>
          {formattedAddress && <p className="public-business-address">{formattedAddress}</p>}
          {businessInfo.phone && (
            <p>
              <strong>Call: </strong>
              <a href={`tel:${businessInfo.phone}`}>{businessInfo.phone}</a>
            </p>
          )}
          {businessInfo.email && (
            <p>
              <strong>Email: </strong>
              <a href={`mailto:${businessInfo.email}`}>{businessInfo.email}</a>
            </p>
          )}
        </div>
      )}

      {sortedCategories.map((cat) => (
        <div key={cat} className="public-category-section">
          <h2 className="public-category-heading">{cat}</h2>
          <ul className="public-menu-list">
            {groupedMenu[cat].map((item) => (
              <li key={item.menuItemId || item.id} className="public-menu-item">
                <div className="public-item-header">
                  <span className="public-item-name">{item.name}</span>
                  <span className="public-item-price">
                    {formatCurrency(item.price || 0, (businessInfo?.currencyCode || "GBP").toUpperCase())}
                  </span>
                </div>
                {item.description && (
                  <div className="public-item-desc">{item.description}</div>
                )}
                <button
                  className="public-buy-btn"
                  onClick={() =>
                    alert(`'${item.name}' added to cart (demo only)`)
                  }
                >
                  Add to Cart
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
